import {HStack, Text} from "@chakra-ui/react";
import {typedMemo} from "../../../utils/typescript";
import {SelectionCheckbox} from "./SelectionCheckbox";
import {useCallback, useMemo} from "react";
import {
  ExtractTableContext,
  ExtractTableItemKey,
  flattenItemsOrGroups,
  GroupState,
  SetSelectedRowKeysFunction,
  TableConfig,
  TableGroup,
} from "../../../hooks/table";
import {BaseTableCell} from "./base";

export const GroupHeaderRowContent = typedMemo(
  <C extends TableConfig>({
    config,
    context,
    groupState,
    depth,
    group,
    numColumns,
    selectedRowKeys,
    setSelectedRowKeys,
    supportsSelection,
  }: {
    config: C;
    context: ExtractTableContext<C>;
    groupState: GroupState<C>;
    depth: number;
    group: TableGroup<C>;

    numColumns: number;
    selectedRowKeys: Set<ExtractTableItemKey<C>>;
    setSelectedRowKeys?: SetSelectedRowKeysFunction<C>;
    supportsSelection: boolean;
  }) => {
    const groupStateEntry = groupState[depth];

    const columnId = groupStateEntry.columnId;
    if (columnId == null) return null;

    const column = config.columns[columnId];
    const groupConfig = column.group;
    if (groupConfig == null) return null;

    const selectableItemKeys = useMemo(
      () =>
        new Set(
          flattenItemsOrGroups(group.itemsOrGroups)
            .filter(item => config.getRowIsSelectable?.(item, context) ?? true)
            .map(item => config.extractUniqueKey(item) as ExtractTableItemKey<C>),
        ),
      [config, context, group.itemsOrGroups],
    );

    const checkedState = useMemo<boolean | "indeterminate">(() => {
      const checkedGroupKeys = selectableItemKeys.intersection(selectedRowKeys);

      if (checkedGroupKeys.size === 0) return false;
      if (checkedGroupKeys.size === selectableItemKeys.size) return true;
      return "indeterminate";
    }, [selectableItemKeys, selectedRowKeys]);

    const onCheckedChange = useCallback(() => {
      // Gmail-style: clear the group if anything in it is selected, else select all.
      setSelectedRowKeys?.(prev =>
        selectableItemKeys.intersection(prev).size > 0
          ? prev.difference(selectableItemKeys)
          : prev.union(selectableItemKeys),
      );
    }, [selectableItemKeys, setSelectedRowKeys]);

    const renderGroupValue = groupConfig.renderHeader;

    const bg = depth === 0 ? "bg.muted" : "bg";

    return (
      <>
        {supportsSelection && (
          <BaseTableCell
            bg={bg}
            // Remove any padding - we instead want to size the checkbox label to
            // the full size of the cell so clicking the checkbox is easier.
            p="0 !important"
          >
            {<SelectionCheckbox checked={checkedState} onCheckedChange={onCheckedChange} />}
          </BaseTableCell>
        )}
        <BaseTableCell colSpan={numColumns} bg={bg} fontWeight="semibold" py={"2 !important"}>
          <HStack gap={1}>
            {groupConfig.showPrefixInHeader !== false && (
              <Text color="fg.muted" whiteSpace="nowrap">
                {column.overrideGroupAndSortLabel ?? column.label}:
              </Text>
            )}
            {renderGroupValue(group.value, context)}
          </HStack>
        </BaseTableCell>
      </>
    );
  },
);
